import React, { useState, useEffect, useRef } from "react";
import Pay from "assets/checkOut/pay.png";
import {
  Row,
  Col,
  Button,
  Spin,
  Divider,
  Card,
  Form,
  Input,
  Select,
  Radio,
  Space,
  message,
  Empty,
  Checkbox,
} from "antd";
import { apiGetMe, apiGetCarts, apiPostOrder } from "api/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { errorHandler } from "utility/functions/errorHandler";
import { paymentMethod } from "utility/options/paymentMethod";

import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { gaHandleBeginCheckout } from "components/ga/index";
import PhoneInput from "components/PhoneInput";

const CheckOut = () => {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const open = useSelector((state) => state.charts.isChartOpen);

  const [radio, setRadio] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [me, setMe] = useState({});
  const [carts, setCarts] = useState([]);
  const [isPayLoading, setIsPayLoading] = useState(false); // # 訂單按鈕 loading
  const [checkedMap, setCheckedMap] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  const genderOptions = [
    { value: 1, label: "男" },
    { value: 2, label: "女" },
    { value: 3, label: "其他" },
  ];

  // # 獲取「用戶」資訊
  const fetchMe = async () => {
    setIsLoading(true);
    try {
      const res = await apiGetMe();
      const { content } = res.data;
      setMe(content);
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoading(false);
    }
  };

  // # 獲取「購物車」資訊
  const fetchCarts = async () => {
    try {
      const isLogin =
        sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;
      let details = [];
      if (isLogin) {
        const res = await apiGetCarts();
        details = res.data.content.details;
      } else {
        details = JSON.parse(localStorage.getItem("carts")) || [];
      }
      setCarts(details);
    } catch (err) {
      console.log("err", err);
    }
  };

  useEffect(() => {
    const isLogin =
      sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;
    if (isLogin) {
      fetchMe();
    }
  }, [sessionStorage._skyFilm_t]);

  useEffect(() => {
    fetchCarts();
  }, []);

  // # 開關購物車抽屜，重新取得購物車內容
  useEffect(() => {
    fetchCarts();
  }, [open]);

  // # 當購物車數量改變，重新設置 form list數量
  useEffect(() => {
    if (carts.length > 0) {
      carts.map((cart, index) => {
        form.setFieldValue(
          ["detail", index],
          Array.from(Array(cart.amount), (_, x) => {
            return { sourceId: cart.sourceId };
          }),
        );
      });
    }
  }, [carts]);

  // # 登出
  const logOut = () => {
    sessionStorage.removeItem("_skyFilm_t");
    setTimeout(() => navigate("/login"));
  };

  // # 付款
  const handlePay = async (value) => {
    if (isPayLoading || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsPayLoading(true);

    const details = form.getFieldsValue().detail?.flat();

    // 驗證是否有訂單內容，防止產生 0 元訂單
    if (!details || details.length === 0) {
      message.error("訂單內容不得為空，請重新確認購物車項目");
      setIsPayLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    try {
      const res = await apiPostOrder({
        details,
        paymentChannel: 2,
        paymentMethod: radio,
      });

      const { content } = res.data;
      if (content && content.content && content.httpMethod) {
        try {
          // 根據 httpMethod 決定處理方式
          if (content.httpMethod === "GET") {
            // GET 方法：使用 navigate 轉址
            navigate(content.content);
          } else if (content.httpMethod === "POST") {
            // POST 方法：使用表單提交
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = content.content;
            const formEl = tempDiv.querySelector("form");

            if (formEl) {
              document.body.appendChild(formEl);

              // 監聽表單提交前的錯誤
              formEl.addEventListener("error", () => {
                console.error("Form submission error");
                setIsPayLoading(false);
              });

              // 設置超時機制，如果 10 秒內沒有跳轉就釋放 loading
              const timeoutId = setTimeout(() => {
                console.warn("Payment submission timeout");
                setIsPayLoading(false);
              }, 10000);

              // 監聽頁面即將離開事件
              const handleBeforeUnload = () => {
                clearTimeout(timeoutId);
              };
              window.addEventListener("beforeunload", handleBeforeUnload);

              formEl.submit();
              // 成功提交後清除事件監聽器
              setTimeout(() => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
              }, 1000);
            } else {
              throw new Error("金流表單格式錯誤");
            }
          } else {
            throw new Error(`不支援的 HTTP 方法: ${content.httpMethod}`);
          }
        } catch (domError) {
          console.error("Payment processing error:", domError);
          setIsPayLoading(false);
          message.error("金流頁面載入失敗，請重試");
        }
      } else {
        throw new Error("金流資訊格式錯誤");
      }

      gaHandleBeginCheckout({ cartItems: details });
    } catch (error) {
      console.error("Payment error:", error);
      errorHandler(error, t);
      setIsPayLoading(false); // API 錯誤時立即關閉 loading
    } finally {
      // 確保即使出錯也能重置 Ref 狀態
      isSubmittingRef.current = false;
    }
  };

  // # 付款失敗
  const handelPayError = (error) => {
    message.error(error.errorFields[0].errors[0]);
  };

  // # 創建虛擬 form & input
  const createHiddenInput = (form, name, value) => {
    const hiddenField = document.createElement("input");
    hiddenField.type = "hidden";
    hiddenField.name = name;
    hiddenField.value = value;
    form.appendChild(hiddenField);
  };

  // # 介接綠界
  const accessEcPay = async (domain, paymentInfo) => {
    return new Promise((resolve) => {
      const form = document.createElement("form");
      const {
        merchantID,
        merchantTradeNo,
        merchantTradeDate,
        paymentType,
        totalAmount,
        tradeDesc,
        itemName,
        returnURL,
        choosePayment,
        checkMacValue,
        encryptType,
        clientBackURL,
        needExtraPaidInfo,
      } = paymentInfo;
      form.method = "POST";
      form.action = domain;
      form.target = "_self"; // 確保在同一頁面跳轉

      createHiddenInput(form, "MerchantID", merchantID);
      createHiddenInput(form, "ClientBackURL", clientBackURL);
      createHiddenInput(form, "MerchantTradeNo", merchantTradeNo);
      createHiddenInput(form, "MerchantTradeDate", merchantTradeDate);
      createHiddenInput(form, "PaymentType", paymentType);
      createHiddenInput(form, "TotalAmount", totalAmount);
      createHiddenInput(form, "TradeDesc", tradeDesc);
      createHiddenInput(form, "ItemName", itemName);
      createHiddenInput(form, "ReturnURL", returnURL);
      createHiddenInput(form, "ChoosePayment", choosePayment);
      createHiddenInput(form, "CheckMacValue", checkMacValue);
      createHiddenInput(form, "EncryptType", encryptType);
      createHiddenInput(form, "NeedExtraPaidInfo", needExtraPaidInfo);

      document.body.appendChild(form);

      // 監聽表單提交事件
      form.addEventListener("submit", () => {
        // 表單提交後，給予短暫延遲確保提交成功，然後解除 loading
        setTimeout(() => {
          setIsPayLoading(false);
          resolve();
        }, 1000); // 1秒後解除 loading，讓用戶看到跳轉過程
      });

      form.submit();
    });
  };

  // # Radio 改變
  const onRadioChange = (e) => {
    setRadio(e.target.value);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const fillIndex = searchParams.get("fillIndex");
  const fillKey = searchParams.get("fillKey");
  const hasFilledRef = useRef(false); // 新增 flag
  const isSubmittingRef = useRef(false); // 新增防止重複送出的 flag

  // 登入後自動帶入會員資料
  useEffect(() => {
    // 條件：已登入、有 fillIndex/fillKey、會員資料已取得、還沒帶入過
    if (
      sessionStorage._skyFilm_t &&
      sessionStorage._skyFilm_t.length > 0 &&
      fillIndex != null &&
      fillKey != null &&
      me &&
      me.name &&
      !hasFilledRef.current
    ) {
      // 1. 勾選 checkbox
      setCheckedMap((prev) => ({
        ...prev,
        [`${fillIndex}_${fillKey}`]: true,
      }));

      // 2. 帶入會員資料
      const sourceId = form.getFieldValue([
        "detail",
        Number(fillIndex),
        Number(fillKey),
        "sourceId",
      ]);
      setTimeout(() => {
        form.setFieldValue(["detail", Number(fillIndex), Number(fillKey)], {
          sourceId,
          name: me.name,
          phone: me.phone,
          email: me.email,
        });
      }, 500);

      // 3. 標記已經帶入過
      hasFilledRef.current = true;

      // 4. 延遲一點再清除 URL 參數（確保 setCheckedMap/setFieldValue 已經執行）
      setTimeout(() => {
        searchParams.delete("fillIndex");
        searchParams.delete("fillKey");
        setSearchParams(searchParams, { replace: true });
      }, 100); // 100ms 通常就夠
    }
    // eslint-disable-next-line
  }, [me, fillIndex, fillKey]);

  // # 勾選帶入會員資料
  const handleCheck = (e, index, key) => {
    const isLogin =
      sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;
    if (!isLogin) {
      // 這裡要帶上 fillIndex 和 fillKey
      navigate(`/login?redirect=/checkout&fillIndex=${index}&fillKey=${key}`);
      return;
    }
    // 記錄勾選狀態
    setCheckedMap((prev) => ({
      ...prev,
      [`${index}_${key}`]: e.target.checked,
    }));
    if (e.target.checked) {
      const { name, phone, email } = me;
      const sourceId = form.getFieldValue(["detail", index, key, "sourceId"]);
      form.setFieldValue(["detail", index, key], {
        sourceId,
        name,
        phone,
        email,
      });
    }
  };

  return (
    <div className="w-full bg-white">
      <Spin spinning={isLoading} size="large" fullscreen />
      <Row style={{ minHeight: "calc(100vh - 64px)" }}>
        <Col
          sm={{ span: 12 }}
          xs={{ span: 24 }}
          className="bg-white pb-20 desktop:pl-32 tablet:px-4 mobile:px-4 text-left"
        >
          <h4 className="text-left text-black mb-0">帳戶 Account</h4>
          <p className="text-black text-left">{me.email}</p>
          <p className="text-left">
            <Button type="link" onClick={logOut} className="pl-0">
              登出
            </Button>
          </p>
          <Divider />
          <h4 className="text-left text-black mb-0">付款 Account</h4>
          <p className="text-SKF-G-002 mb-5">所有交易都受到安全加密保護。</p>
          <Radio.Group onChange={onRadioChange} value={radio}>
            <Space direction="vertical">
              {paymentMethod
                .filter(
                  (method) =>
                    method.value === 1 ||
                    method.value === 6 ||
                    method.value === 3,
                )
                .map((method) => (
                  <Radio value={method.value} key={method.id}>
                    {method.name}
                  </Radio>
                ))}
            </Space>
          </Radio.Group>
        </Col>
        <Col
          sm={{ span: 12 }}
          xs={{ span: 24 }}
          className="bg-SKF-G-000 pb-20 desktop:pr-32 desktop:pl-8 mobile:px-4 tablet:px-4"
          // style={{
          //   display: "flex",
          //   flexDirection: "column",
          //   justifyContent: "center",
          //   minHeight: "calc(100vh - 64px)", // 讓內容區塊填滿
          // }}
        >
          <h4 className="text-left text-black mb-0">
            {currentLanguage === "zhTW" && "產品"} Items
          </h4>

          {/* 購物車空的時候顯示 */}
          {carts.length === 0 ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="w-full mt-10 mx-0 py-20 rounded-lg border-dashed border-2 border-SKF-G-001"
                description={
                  t("checkOut:noItems") || "目前沒有任何需要結帳的產品"
                }
              />
            </div>
          ) : null}

          {/* 購物車有東西的時候顯示 */}
          <Form
            layout="vertical"
            form={form}
            onFinish={handlePay}
            onFinishFailed={handelPayError}
            scrollToFirstError={{ behavior: "smooth", block: "center" }}
          >
            {carts &&
              carts.map((cart, index) => {
                return (
                  <Card
                    key={index}
                    title={
                      <Row gutter={16} wrap>
                        <Col xs={24} sm={19} className="text-left">
                          <p className="whitespace-normal">{cart.groupTitle}</p>
                          <p className="whitespace-normal mobile:text-base font-semibold">
                            {cart.title}
                          </p>
                        </Col>
                        <Col xs={24} sm={5} className="flex justify-end mb-2">
                          <div>
                            <p className="bg-SKF-Blue-001 text-white p-2 rounded-lg">
                              數量：{cart.amount}
                            </p>
                            <p className="mt-2 text-base">
                              NT${cart.amount * cart.price}
                            </p>
                          </div>
                        </Col>
                      </Row>
                    }
                    className="my-3 py-4 [&_.ant-card-head-wrapper]:items-start"
                    extra={<></>}
                  >
                    <Form.List
                      name={["detail", index]}
                      // initialValue={Array.from(Array(cart.amount), (_, x) => {
                      //   return { sourceId: cart.sourceId };
                      // })}
                    >
                      {(fields) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <Row gutter={16} key={key}>
                              <Col span={24}>
                                <Space className="mb-4 w-full [&_.ant-space-item:first-child]:grow">
                                  <p className="text-left justify-between ">
                                    票券 {key + 1}-{cart.amount}
                                  </p>
                                  <Checkbox
                                    checked={
                                      checkedMap[`${index}_${key}`] || false
                                    }
                                    onChange={(e) => handleCheck(e, index, key)}
                                  >
                                    帶入會員資料
                                  </Checkbox>
                                </Space>
                              </Col>
                              <Col span={24}>
                                <Form.Item
                                  name={[name, "name"]}
                                  label="Name"
                                  rules={[{ required: true }]}
                                >
                                  <Input
                                    className="p-2"
                                    size="large"
                                    placeholder="請輸入持票人姓名"
                                  ></Input>
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  name={[name, "gender"]}
                                  label="Gender"
                                  rules={[{ required: true }]}
                                >
                                  <Select
                                    options={genderOptions}
                                    size="large"
                                    className="[&_.ant-select-selector]:p-2 [&_.ant-select-arrow]:h-0 text-left"
                                    placeholder="請選擇持票人性別"
                                  ></Select>
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  name={[name, "age"]}
                                  label="Age"
                                  rules={[{ required: true }]}
                                >
                                  <Input
                                    className="p-2"
                                    size="large"
                                    placeholder="請輸入持票人年齡"
                                  ></Input>
                                </Form.Item>
                              </Col>
                              <Col span={24}>
                                <Form.Item
                                  name={[name, "phone"]}
                                  label="Mobile Phone"
                                  rules={[
                                    { required: true },
                                    // {
                                    //   pattern: /^09\d{8}$/,
                                    //   message: t(
                                    //     "請輸入正確的手機號碼（09開頭，共10碼）"
                                    //   ),
                                    // },
                                    //                                 {
                                    //   pattern: /^\+8869\d{8}$/,
                                    //   message: t("請輸入正確的手機號碼（+886開頭，後面接9位數字）"),
                                    // },
                                  ]}
                                >
                                  <PhoneInput
                                    className="p-0 [&_.ant-input]:p-2"
                                    size="large"
                                    placeholder="請輸入持票人手機號碼"
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={24}>
                                <Form.Item
                                  name={[name, "email"]}
                                  label="Email"
                                  rules={[
                                    { required: true },
                                    {
                                      type: "email",
                                    },
                                  ]}
                                >
                                  <Input
                                    className="p-2"
                                    size="large"
                                    placeholder="請輸入持票人Email"
                                  ></Input>
                                </Form.Item>
                              </Col>
                            </Row>
                          ))}
                        </>
                      )}
                    </Form.List>
                  </Card>
                );
              })}
            <div
              className="fixed right-0 bottom-0 bg-white w-full border-solid border border-SKF-G-001 pt-4 pb-8 mobile:pb-2 tablet:pb-4 z-50"
              style={{
                minHeight: "80px",
                paddingBottom: "env(safe-area-inset-bottom, 16px)",
              }}
            >
              <Row>
                <Col span={24} className="mb-4 mobile:mb-0">
                  <Form.Item>
                    <Button
                      type="primary"
                      className="bg-SKF-Blue-001 w-5/6 desktop:w-4/5"
                      htmlType="submit"
                      loading={isPayLoading}
                      disabled={carts.length === 0}
                    >
                      馬上付款 Pay
                    </Button>
                  </Form.Item>
                </Col>
                <Col
                  sm={{ span: 12 }}
                  xs={{ span: 24 }}
                  className="desktop:text-left mobile:text-center tablet:text-center"
                >
                  <p className="text-SKF-Blue-001 desktop:pl-36">
                    票券問題請聯繫：support@rftexp.com
                  </p>
                </Col>
                <Col
                  sm={{ span: 12 }}
                  xs={{ span: 24 }}
                  className="desktop:text-right mobile:text-center tablet:text-center desktop:pr-36"
                >
                  <p className="text-SKF-G-002">Skyline Film 保留所有權利</p>
                </Col>
              </Row>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default CheckOut;
